import React from "react";
import moment from "moment";

import { useApolloClient } from "@apollo/client";

import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Toolbar } from "primereact/toolbar";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";

/** contexts */
import { useSettings } from "../../context/settings";

/** QUERIES MUTATIONS */
import CREATE_MEDICINE, { UPDATE_MEDICINE } from "../../provider/Medicines/mutation";
import { MEDICINES } from "../../provider/Medicines/query";

/** utils */
import { getGraphsqErros } from "../../utilities/functions";

const Crud = () => {
    const settings = useSettings();
    const apollo = useApolloClient();

    const toast = React.useRef<any>(null);
    const dt = React.useRef<any>(null);

    const [records, setRecords] = React.useState<iMedicine[]>([]);
    const [record, setRecord] = React.useState<iMedicine>();

    const [loading, setLoading] = React.useState<boolean>(false);
    const [recordDialog, setRecordDialog] = React.useState<boolean>(false);
    const [deleteRecordDialog, setDeleteRecordDialog] = React.useState<boolean>(false);

    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [globalFilter, setGlobalFilter] = React.useState<any>(null);

    let emptyRecord: iMedicine = {
        name: "",
        box: "",
        need: "",
        stock: "",
        due_date: "",
    };

    const loadRecords = (cleanRecord: boolean = false) => {
        setLoading(true);

        apollo
            .query({
                query: MEDICINES,
                variables: {},
                fetchPolicy: "no-cache",
            })
            .then(response => {
                const data: iMedicine[] = response.data.medicines;

                setRecords(data.filter(i => !i.is_deleted));
                setLoading(false);
            })
            .catch(error => {
                setRecords([]);
                settings?.setErrorMessage(getGraphsqErros(error));
                settings?.setVisibleError(true);
                setLoading(false);
            });
    };

    const openNew = () => {
        setRecord(emptyRecord);
        setSubmitted(false);
        setRecordDialog(true);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setRecordDialog(false);
    };

    const hideDeleteRecordDialog = () => {
        setDeleteRecordDialog(false);
    };

    const saveRecord = () => {
        setSubmitted(true);

        if (!record?.name || !record?.box || !record.stock || !record.need) {
            return;
        }

        const _record: any = { ...record };

        delete _record.__typename;

        if (_record.stock) _record.stock = parseInt(`${_record.stock}`);
        if (_record.need) _record.need = parseInt(`${_record.need}`);

        setLoading(true);
        let mutation = CREATE_MEDICINE;

        if (record?.id) {
            _record.id = parseInt(`${_record.id}`);
            mutation = UPDATE_MEDICINE;

            delete _record.key;
        }

        apollo
            .mutate({
                mutation,
                variables: {
                    data: {
                        ..._record,
                    },
                },
            })
            .then(response => {
                setLoading(false);

                toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro salvo com sucesso.", life: 3000 });

                loadRecords();
                setRecordDialog(false);
                setRecord(emptyRecord);
            })
            .catch(error => {
                setLoading(false);
                settings.setErrorMessage(getGraphsqErros(error));
                settings.setVisibleError(true);
            });
    };

    const editRecord = (record: iMedicine) => {
        const _record: iMedicine = { ...record };

        if (_record.due_date) _record.due_date = moment(_record.due_date).utc().format("YYYY-MM-DD");
        setRecord({ ..._record });
        setRecordDialog(true);
    };

    const confirmDeleteRecord = (record: any) => {
        setRecord(record);
        setDeleteRecordDialog(true);
    };

    const deleteRecord = () => {
        apollo
            .mutate({
                mutation: UPDATE_MEDICINE,
                variables: {
                    data: {
                        id: parseInt(`${record?.id}`),
                        is_deleted: true,
                    },
                },
            })
            .then(response => {
                let _records = records.filter((val: any) => val.id !== record?.id);
                setRecords(_records);
                setDeleteRecordDialog(false);
                setRecord(emptyRecord);
                toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro deletado", life: 3000 });
            })
            .catch(error => {
                setLoading(false);
                settings.setErrorMessage(getGraphsqErros(error));
                settings.setVisibleError(true);
            });
    };

    const onInputChange = (e: any, name: string) => {
        let val = (e.target && e.target.value) || "";

        let _record: any = { ...record };

        _record[name] = val;

        setRecord(_record);
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label="Novo" icon="pi pi-plus" className="p-button-success mr-2 " onClick={openNew} />
                {/* <Button
                    label="Delete"
                    icon="pi pi-trash"
                    className="p-button-danger mb-2"
                    onClick={confirmDeleteSelected}
                    disabled={!selectedRecords || !selectedRecords.length}
                /> */}
            </React.Fragment>
        );
    };

    const nameBodyTemplate = (rowData: iMedicine) => {
        return (
            <>
                <span className="p-column-title">Nome</span>
                {rowData.name}
            </>
        );
    };

    const needBodyTemplate = (rowData: iMedicine) => {
        return (
            <>
                <span className="p-column-title">Necessidade</span>
                {rowData.need}
            </>
        );
    };

    const stockBodyTemplate = (rowData: iMedicine) => {
        return (
            <>
                <span className="p-column-title">Estoque</span>
                {rowData.stock}
            </>
        );
    };

    const boxBodyTemplate = (rowData: iMedicine) => {
        return (
            <>
                <span className="p-column-title">Caixa</span>
                {rowData.box}
            </>
        );
    };

    const dueDateBodyTemplate = (rowData: iMedicine) => {
        return (
            <>
                <span className="p-column-title">Vencimento</span>
                {rowData.due_date ? moment(rowData?.due_date).utc().format("DD/MM/YYYY") : "-"}
            </>
        );
    };

    const actionBodyTemplate = (rowData: iMedicine) => {
        return (
            <div className="actions">
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-info mr-2" onClick={() => editRecord(rowData)} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-danger mt-0" onClick={() => confirmDeleteRecord(rowData)} />
            </div>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">{loading ? "Carregando medicamentos..." : "Medicamentos"}</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText className="min-w-full" type="search" onInput={e => setGlobalFilter(e.currentTarget.value)} placeholder="Procurar..." />
            </span>
        </div>
    );

    const recordDialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" disabled={loading} className="p-button-text text-red-500" onClick={hideDialog} />
            <Button label={loading ? "Salvando..." : "Salvar"} disabled={loading} icon="pi pi-check" className="p-button-text" onClick={saveRecord} />
        </>
    );

    const deleteRecordDialogFooter = (
        <>
            <Button label="Não" icon="pi pi-times" className="p-button-text text-red-500" onClick={hideDeleteRecordDialog} />
            <Button label="Sim, excluir!" icon="pi pi-check" className="p-button-text" onClick={deleteRecord} />
        </>
    );

    React.useEffect(() => {
        loadRecords(true);
    }, []);

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <Toolbar className="mb-4" left={leftToolbarTemplate}></Toolbar>

                    <DataTable
                        ref={dt}
                        value={records}
                        dataKey="id"
                        paginator
                        stripedRows={true}
                        rowHover
                        showGridlines
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
                        globalFilter={globalFilter}
                        emptyMessage="Nenhum registro encontrado"
                        header={header}
                        responsiveLayout="scroll">
                        <Column field="name" header="Nome" sortable body={nameBodyTemplate} headerStyle={{}}></Column>
                        <Column field="need" header="Necessidade" body={needBodyTemplate} sortable headerStyle={{}}></Column>
                        <Column field="stock" header="Estoque" body={stockBodyTemplate} sortable headerStyle={{}}></Column>
                        <Column field="box" header="Caixa" body={boxBodyTemplate} sortable headerStyle={{}}></Column>
                        <Column field="due_date" header="Vencimento" body={dueDateBodyTemplate} sortable headerStyle={{}}></Column>

                        <Column header="Ações" className="flex flex-row align-items-start justify-content-start" body={actionBodyTemplate}></Column>
                    </DataTable>

                    {/* Modal adicionar editar */}
                    <Dialog
                        visible={recordDialog}
                        style={{ width: "550px" }}
                        header={record?.id ? "Detalhes do Medicamento" : "Novo Medicamento"}
                        modal
                        className="p-fluid"
                        footer={recordDialogFooter}
                        onHide={hideDialog}>
                        <div className="flex flex-column md:flex-row flex-wrap">
                            <div className="field col-12 md:col-12">
                                <label htmlFor="name">* Nome</label>
                                <InputText
                                    id="name"
                                    value={record?.name}
                                    onChange={e => onInputChange(e, "name")}
                                    required
                                    autoFocus
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.name })}
                                />
                                {submitted && !record?.name && <small className="p-invalid">Nome é obrigatório.</small>}
                            </div>
                        </div>

                        <div className="flex flex-column md:flex-row flex-wrap">
                            <div className="field col-12 md:col-6">
                                <label htmlFor="need">* Necessidade</label>
                                <InputText
                                    id="need"
                                    value={record?.need}
                                    onChange={e => onInputChange(e, "need")}
                                    required
                                    type={"number"}
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.need })}
                                />
                                {submitted && !record?.need && <small className="p-invalid">Necessidade é obrigatório.</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label htmlFor="need">* Estoque</label>
                                <InputText
                                    id="stock"
                                    value={record?.stock}
                                    onChange={e => onInputChange(e, "stock")}
                                    required
                                    type={"number"}
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.stock })}
                                />
                                {submitted && !record?.stock && <small className="p-invalid">Estoque é obrigatório.</small>}
                            </div>
                        </div>

                        <div className="flex flex-column md:flex-row flex-wrap">
                            <div className="field col-12 md:col-6">
                                <label htmlFor="due_date"> Data de Vencimento</label>
                                <InputText
                                    id="due_date"
                                    type={"date"}
                                    value={record?.due_date}
                                    onChange={e => onInputChange(e, "due_date")}
                                    className={classNames("p-3")}
                                />
                            </div>

                            <div className="field col-12 md:col-6">
                                <label htmlFor="box">* Nº Caixa</label>
                                <InputText
                                    id="box"
                                    value={record?.box}
                                    onChange={e => onInputChange(e, "box")}
                                    required
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.box })}
                                />
                                {submitted && !record?.box && <small className="p-invalid">Nº Caixa é obrigatório.</small>}
                            </div>
                        </div>
                    </Dialog>

                    {/* Modal remover registro unico */}
                    <Dialog
                        visible={deleteRecordDialog}
                        // style={{ width: "450px" }}
                        header="Atenção"
                        modal
                        footer={deleteRecordDialogFooter}
                        onHide={hideDeleteRecordDialog}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
                            {record && (
                                <span>
                                    Tem certeza de que deseja excluir <b>{record?.name}</b>?
                                </span>
                            )}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default Crud;
